import { useNavigate } from 'react-router-dom';
import { useReveal } from '@/hooks/useReveal';
import PageLoader from '@/components/PageLoader';
import HeroCrumbs from '@/components/HeroCrumbs';
import Button from '@/components/ui/Button';
import w from './SafetyEnv.module.scss';
import { GUIDE_ICON } from '@/components/GuideIcons';

// ============================================================================
//  안전 — 한랭작업 관리 (예정)  (/safety/cold)
//  [2026-10-08] 기획서 v7 3-4. ①문제·대상 ②예정 구성 ③예정 기능 ④개발 상태. 개발예정 → 준비중 위주.
//  [2026-10-22 사용자] ★본문 화이트화(SafetyEnv.module). 히어로 다크 유지 / 본문 화이트. ②셰브론 ③원형뱃지.
//    주의(block 223): 제품·산정기준 확정 전 출시·인증·법적 적합을 단정하지 않는다.
// ============================================================================

const HERO_IMG = '/images/hero/hero-safety.jpg';
const SLOGAN = '온도·습도·풍속을 종합한 한랭지수로 겨울철·냉동·옥외 작업의 위험을 측정하고 단계별로 관리하는 한랭작업 안전 솔루션입니다. (개발 예정)';
const INQ = '/support/inquiry?product=' + encodeURIComponent('한랭작업 관리') + '&from=/safety/cold';

const Ic = ({ n }: { n: string }) => <div className={w.iconWrap}>{GUIDE_ICON[n]}</div>;
const Reveal = ({ className = '', children }: { className?: string; children: React.ReactNode }) => {
  const ref = useReveal<HTMLDivElement>();
  return <div className={`${className} anim-scroll`} ref={ref}>{children}</div>;
};
function Soon({ label }: { label: string }) {
  return (
    <div className={w.soon} role="img" aria-label={`${label} 준비중`}>
      <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>
      <span>{label} · 준비중</span>
    </div>
  );
}

export default function SafetyCold() {
  const navigate = useNavigate();
  const nav = (to: string) => navigate(to);
  return (
    <main className={w.page}>
      <PageLoader />
      <header className={w.hero}>
        <div className={w.heroBg} style={{ backgroundImage: `url(${HERO_IMG})` }} />
        <div className={w.heroOverlay} />
        <HeroCrumbs items={['HOME', '안전', '한랭작업 관리']} />
        <div className={w.heroInner}>
          <h1 className={w.heroTitle}>한랭작업 관리</h1>
          <p className={w.heroSub}>{SLOGAN}</p>
        </div>
      </header>

      {/* ① 문제·대상 */}
      <section className={w.section}>
        <div className={w.inner}>
          <Reveal>
            <p className={w.eyebrow}>THE PROBLEM</p>
            <h2 className={w.h2}>한랭작업, 저체온·동상 위험이 있습니다</h2>
            <p className={w.lead}>옥외·냉동·물류·건설 등 저온 환경 작업은 저체온증·동상 위험이 큽니다. 온도·습도·풍속을 함께 보는 한랭작업 위험관리를 준비하고 있습니다.</p>
            <a className={w.moreLink} onClick={() => nav('/safety')}>안전 솔루션 보기 →</a>
          </Reveal>
        </div>
      </section>

      {/* ② 예정 구성 — 셰브론 배너(센서→단말→플랫폼) */}
      <section className={`${w.section} ${w.alt}`}>
        <div className={w.inner}>
          <Reveal>
            <p className={w.eyebrow}>PLANNED SYSTEM</p>
            <h2 className={w.h2}>예정 구성</h2>
            {/* [2026-10-22 사용자] pillList 파기 → 승인된 셰브론으로. 이 페이지의 유일 다이어그램(센서→단말→플랫폼). 색 뚜렷. */}
            <div className={w.chevron}>
              {[
                { n: '01', t: '센서', i: 'gauge', d: '온·습도·풍속 센서로 한랭 위험 요소를 측정(예정).', c: w.cTeal },
                { n: '02', t: '단말', i: 'monitor', d: '현장 단말이 측정값을 표시·전송(예정).', c: w.cBlue },
                { n: '03', t: '플랫폼', i: 'cycle', d: '통합 관제 플랫폼에 편입해 관제(예정).', c: w.cIndigo },
              ].map((x) => (
                <div key={x.n} className={`${w.chevronStep} ${x.c}`}>
                  <div className={w.chevronIco}>{GUIDE_ICON[x.i]}</div>
                  <span className={w.chevronNum}>{x.n}</span>
                  <h3>{x.t}</h3><p>{x.d}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ③ 예정 기능 — 채움 원형 뱃지 */}
      <section className={w.section}>
        <div className={w.inner}>
          <Reveal>
            <p className={w.eyebrow}>PLANNED FEATURES</p>
            <h2 className={w.h2}>예정 기능</h2>
            {/* 단순 틴트 카드(다이어그램 아님) */}
            <div className={`${w.grid} ${w.grid3}`}>
              <div className={`${w.card} ${w.cSky} ${w.solid}`}><Ic n="gauge" /><h3>한랭지수</h3><p>온도·습도·풍속 기반 한랭지수 산정(예정).</p></div>
              <div className={`${w.card} ${w.cAmber} ${w.solid}`}><Ic n="bell" /><h3>단계 알림</h3><p>위험 단계별 알림(예정).</p></div>
              <div className={`${w.card} ${w.cTeal} ${w.solid}`}><Ic n="doc" /><h3>기록·보고·TV</h3><p>기록·보고서·현장 TV 표시(예정).</p></div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ④ 개발 상태 — CTA 틴트밴드(상단 시안 악센트 내장) */}
      <section className={w.finalCta}>
        <div className={w.inner}>
          <Reveal>
            <h2 className={w.h2}>개발 상태</h2>
            <p className={w.lead}>제품과 산정 기준은 확정 전입니다. 확정·검토 중 기능을 구분해 안내하며, 출시·인증·법적 적합 여부는 확정 후 공지합니다.</p>
            <div className={w.ctaRow}>
              <Button variant="pill" onClick={() => nav(INQ)}>개발 문의</Button>
              <a className={w.ctaGhost} onClick={() => nav(INQ)}>출시 알림 신청</a>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
